import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import SEO from '../components/SEO';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
  category: string;
}

function FAQAccordion({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
          {item.answer}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const faqs: FAQItem[] = [
    // Valuation & Methodology
    {
      category: 'Valuation & Methodology',
      question: 'How are the values calculated?',
      answer: (
        <>
          <p className="mb-3">
            Our valuations use a combination of the <strong>sales comparison and cost approach</strong> methodologies — the same framework used by certified appraisers in the petroleum retail industry. To project fuel performance, we factor in local tracked retail prices, spot pricing, oil futures, local costs, and the distance to the nearest branded rack. Fuel volume estimates account for existing competitors, hyperfueler influence, local TAM growth, and proximity to major highways. C-store sales projections are derived from capture rates based on traffic counts, local population, and projected fuel volume, and further adjusted for store layout and competitive density. Margins are modeled relative to anticipated sales mix, and all performance data is cross-referenced against cohorts of comparable station performance sets in the region.
          </p>
          <p className="mb-3">
            To determine asset value, we estimate the replacement cost new (RCN) of each component (land, building, canopy, fueling equipment, underground storage tanks, and FF&E), applying depreciation based on age and condition. Through multi-regression analysis, we evaluate the latest confirmed retail gas station sales in the state and consider the proportion of real property value to the total going-concern to predict a final sales price. Once the regressions are performed and reconciled, a series of risk factor assessments make adjustments to the identified gross profit multiplier — accounting for location risk, bundle of rights, brand strength, environmental exposure, and operational efficiency — to arrive at a station-specific concluded value.
          </p>
          <p>
            Finally, we employ a residual method to calculate business value, defined as the difference between the total going-concern value and the sum of tangible assets. All of our models are trained on real transaction data from Southern California and incorporate market-specific factors like fuel volume, c-store revenue, traffic counts, and brand affiliation.
          </p>
        </>
      ),
    },
    {
      category: 'Valuation & Methodology',
      question: 'What do the three value indicators mean (Cost to Compete, Market Value, Entrepreneurial Value)?',
      answer: (
        <>
          <p className="mb-3">
            <strong>Cost to Compete</strong> represents the replacement cost new (RCN) of all assets plus indirect costs, less an entrepreneurial incentive discount — essentially what it would cost to build a comparable facility from the ground up. It is not unusual for this figure to exceed the market value of existing stations, as construction costs have escalated significantly in recent years and economies of scale generally require new builds to be designed for high-volume capacity to justify the investment. This indicator also does not account for any fuel provider incentive monies, signing bonuses, or brand allowances that a new-build operator might negotiate, which can materially offset development costs. It serves as a useful ceiling reference — the upper bound of what a rational buyer would pay before choosing to build new instead.
          </p>
          <p className="mb-3">
            <strong>Estimated Property Value</strong> is the predicted final sales price — what the property would likely trade for in a fair, arm's-length transaction based on its current income profile, asset condition, and market positioning. This is the primary concluded value and the number most relevant for acquisition screening, disposition planning, and lending conversations. The detailed asset breakdown in your report (land, improvements, equipment, and business value) ties to this figure, as it represents the value most likely to be referenced in collateralization and underwriting scenarios.
          </p>
          <p>
            <strong>Entrepreneurial Value</strong> reflects the operational uplift an experienced or strategically motivated operator could achieve beyond current performance. It accounts for above-market management, brand optimization, and revenue enhancement opportunities. Beyond operations, some stations offer market share access, territorial positioning, or proximity to intellectual property (such as proprietary supply agreements or exclusive brand territories) that a specific buyer may find uniquely valuable. In markets with significant barriers to entry — permitting restrictions, zoning limitations, or environmental constraints — the underlying real estate value can be inflated well above what the income alone would suggest, because the station represents one of a limited number of opportunities to compete in that trade area. The Entrepreneurial Value captures this strategic premium.
          </p>
        </>
      ),
    },
    {
      category: 'Valuation & Methodology',
      question: 'How accurate are the valuations?',
      answer: 'Our model is calibrated against actual sale transactions and produces results consistent with professional appraisal ranges. That said, this is a data-driven estimate — not a certified appraisal. Automated valuations are best used for screening acquisitions, benchmarking your portfolio, preparing for negotiations, or getting a second opinion on a deal. For lending or legal purposes, a licensed appraiser is recommended.',
    },
    {
      category: 'Valuation & Methodology',
      question: 'What if I don\'t agree with a value?',
      answer: 'Valuations are only as good as the inputs and the data behind the models. If a value seems off, start by double-checking your inputs — particularly fuel volume, c-store revenue, and property condition, as small changes in these fields can meaningfully shift the result. That said, we recognize our models may not fully capture hyper-local market dynamics along a specific corridor, and our land value estimates may not reflect recent redevelopment activity, zoning bonuses, or entitlement changes that can significantly elevate underlying real estate values — which in turn support varying multiples. If something doesn\'t look right, we want to hear about it. Reach out to us and we\'ll walk through the output together — your feedback helps us improve the platform for everyone.',
    },

    // Data & Coverage
    {
      category: 'Data & Coverage',
      question: 'What areas do you currently cover?',
      answer: 'We currently cover gas stations in Orange County, Riverside County, and San Bernardino County in Southern California. We are actively expanding to additional California counties and other states. Subscribe to our newsletter to be notified when new markets go live.',
    },
    {
      category: 'Data & Coverage',
      question: 'I don\'t see a gas station on the list. How can I add it?',
      answer: (
        <>
          <p>
            Our property database is sourced from public records and may not include every station. Valuations can only be run on properties currently in our database. If your station isn't listed, <Link to="/contact-us" className="text-blue-600 hover:underline">contact us</Link> and we'll get it added — typically within a few business days.
          </p>
        </>
      ),
    },
    {
      category: 'Data & Coverage',
      question: 'What data do I need to run a valuation?',
      answer: 'At minimum, you\'ll need the property address, building size, lot size, number of fuel pumps, annual fuel gallons, and monthly c-store revenue. The more data you provide — including operating expenses, traffic counts, year built, and asset conditions — the higher the confidence score and the more accurate the result. For some stations in our database, we already have physical attributes on file, and for select properties we can provide estimated financial projections to help get you started. However, this is not available for every station, and we always recommend providing your own financial data where possible — our projections are modeled estimates, and while they are informed by real market data, they are never as reliable as your actual operating numbers.',
    },
    {
      category: 'Data & Coverage',
      question: 'Do you keep my data? Is it secure?',
      answer: (
        <>
          <p className="mb-3">
            Yes, your valuation data is stored securely in your account so you can access it from your dashboard at any time. We use Supabase (built on PostgreSQL) with row-level security — meaning only you can see your own valuations.
          </p>
          <p>
            We do not sell or share your data with third parties. Your financial inputs and valuation results are private to your account. See our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> for full details.
          </p>
        </>
      ),
    },

    // Pricing & Billing
    {
      category: 'Pricing & Billing',
      question: 'How does pricing work?',
      answer: 'Individual valuation reports are $100 each on the Regular plan. Premium subscribers pay $500/month and receive 10 valuation credits included, with discounted rates for additional reports. Enterprise pricing is custom. We\'re currently in beta, so these are introductory rates.',
    },
    {
      category: 'Pricing & Billing',
      question: 'Is there a free trial?',
      answer: 'Signing up is free and gives you access to the dashboard, market insights, and newsletter. Your first valuation report requires a $100 payment. If you\'re still on the fence and want to see the platform in action before committing, reach out to us — we may be able to arrange a complimentary valuation run so you can evaluate the output firsthand.',
    },
    {
      category: 'Pricing & Billing',
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit and debit cards through Stripe. Payment is processed securely at checkout — we never store your card details on our servers.',
    },

    // Account & Platform
    {
      category: 'Account & Platform',
      question: 'Do I need an account to run a valuation?',
      answer: 'You can fill out the valuation form without an account, but you\'ll need to sign in (via Google) to view and save your results. This ensures your valuations are securely stored and accessible from your dashboard.',
    },
    {
      category: 'Account & Platform',
      question: 'Can I compare multiple properties?',
      answer: 'Yes. The dashboard includes a comparison matrix that lets you benchmark up to 4 properties side-by-side. Premium and Enterprise subscribers also get access to portfolio-level analytics and advanced benchmarking tools.',
    },
    {
      category: 'Account & Platform',
      question: 'Who is this platform for?',
      answer: 'Fuel Central is built for gas station buyers, sellers, brokers, lenders, and operators. Historically, reliable petroleum retail data has been fragmented and difficult to source — we built this platform to help bridge that gap. Whether you\'re evaluating a potential acquisition, trying to determine a realistic asking price, underwriting a commercial loan, or benchmarking the performance of an existing portfolio, the platform is designed to remove the guesswork. By leveraging advanced modeling to predict a subject property\'s likely sales price, Fuel Central gives every stakeholder in the market access to the data they need to make informed, confident decisions.',
    },
  ];

  const categories = ['All', ...Array.from(new Set(faqs.map(f => f.category)))];
  const filteredFaqs = activeCategory === 'All' ? faqs : faqs.filter(f => f.category === activeCategory);

  return (
    <>
      <SEO
        title="FAQ — Frequently Asked Questions | Fuel Central"
        description="Common questions about gas station valuations, pricing, methodology, data coverage, and using the Fuel Central platform."
        keywords="gas station valuation FAQ, fuel station appraisal questions, gas station market value help"
      />

      <div className="w-full">
        {/* Hero */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
              <p className="text-xl text-blue-100">
                Everything you need to know about gas station valuations and the Fuel Central platform.
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="container mx-auto px-4 pt-10">
          <div className="max-w-3xl mx-auto flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setOpenIndex(null);
                }}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-colors',
                  activeCategory === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-3xl mx-auto space-y-3">
            {filteredFaqs.map((faq, index) => (
              <FAQAccordion
                key={faq.question}
                item={faq}
                isOpen={openIndex === index}
                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-gray-50">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
              <p className="text-gray-600 mb-6">
                We're happy to help. Reach out and we'll get back to you as soon as possible.
              </p>
              <Link
                to="/contact-us"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
